import {
	Autocomplete,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid2,
	Popper,
	Stack,
	TextField,
	Typography
} from "@mui/material";

import IconButton from "@mui/material/IconButton";
import {
	Add,
	AlternateEmail,
	ArrowBack,
	Block,
	Campaign,
	Cancel,
	ChevronRight,
	Close,
	CreditScore,
	Delete,
	DomainVerification,
	FolderSpecial,
	Language,
	Send,
	Check,
} from "@mui/icons-material";
import {Link, useNavigate, useParams} from "react-router-dom";
import StatCard from "@components/StatCard";
import {useEffect, useState} from "react";
import {urls} from "@routes";
import {useMutation, useQuery} from "@tanstack/react-query";
import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {useSnackbar} from "notistack";
import {useDialogs} from "@toolpad/core";
import ServerSideDataTable from "@components/ServerSideDataTable";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import CopyButton from "@components/CopyButton";
import { Downloading } from "@mui/icons-material";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

export interface WorkspaceDetail {
	id: number;
	name: string;
}

interface BillingHistoryItem {
	subscription_id: string;
	workspace_id: string;
	workspace_name: string;
	plan_name: string;
	start_date: string;
	expiry_date: string;
	plan_amount: number;
	plan_status: string;
	last_plan: boolean;
	cancel_date: string;
}

interface PageData {
	status_code: number
	status_text: string

	email: string
	username: string
	user_id: number

	total_workspaces: number
	total_connected_domains: number
	total_email_subdomains: number
	total_email_ids: number
	total_campaigns: number
	total_emails_sent: number
	monthly_total_emails_remaining: number
	user_verified: boolean
	cancelled_due_to_bounce_count: number
	workspace_details: WorkspaceDetail[]

	campaigns_blocked: boolean
	early_access: boolean
	spam_ban: boolean
	is_admin: boolean
	bounce_limit: number
}

export default function AdminUserDetails() {
	const {userId} = useParams();
	const { user } = useAuth();
	const navigate = useNavigate();
	const {enqueueSnackbar} = useSnackbar();
	const dialogs = useDialogs();

	const [pageData, setPageData] = useState<PageData>();
	const [billingHistory, setBillingHistory] = useState<BillingHistoryItem[]>([]);
	const [bounceLimit, setBounceLimit] = useState<string>("");

	// Column definitions for billing history table
	const billingHistoryColumnDefs: ColDef[] = [
		{field: "workspace_name", headerName: "Workspace", flex: 1, minWidth: 150},
		{field: "plan_name", headerName: "Plan Name", flex: 1, minWidth: 200},
		{field: "start_date", headerName: "Start Date", width: 120},
		{field: "expiry_date", headerName: "End Date", width: 120},
		{field: "cancel_date", headerName: "Cancelled Date", width: 120},
		{
			field: "plan_amount",
			headerName: "Plan Amount (USD)",
			width: 150,
			valueFormatter: (params: ValueFormatterParams) => `$${params.value}`
		},
		{field: "plan_status", headerName: "Plan Status", width: 120},
		{
			field: "last_plan",
			headerName: "Last Plan",
			width: 100,
			valueFormatter: (params: ValueFormatterParams) => params.value ? "Yes" : "No"
		},
	];
	const [openAddCredit, setOpenAddCredit] = useState(false)
	const [workspace, setWorkspaces] = useState<WorkspaceDetail | null>(null);
	const [openAddUpdatePost, setOpenAddUpdatePost] = useState(false)
	const [exportLoading, setExportLoading] = useState(false);

	// Query to fetch user details.
	const pageDataQuery = useQuery({
		queryKey: ["adminUserDetailsPageDataQuery", userId],
		queryFn: () => authenticateAndFetchData(`/admin/get-user-details/?user_id=${userId}`),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	});
	useEffect(() => {
		if (pageDataQuery.data) {
			setPageData(pageDataQuery.data.data);
			setBounceLimit(String(pageDataQuery.data.data.bounce_limit ?? 5));
		}
	}, [pageDataQuery.data]);

	// Query to fetch billing history.
	const billingHistoryQuery = useQuery({
		queryKey: ["adminUserBillingHistoryQuery", userId],
		queryFn: () => authenticateAndFetchData(`/admin/user-billing-history/?user_id=${userId}&page=1&page_size=100`),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
		enabled: !!userId,
	});
	useEffect(() => {
		if (billingHistoryQuery.data) {
			setBillingHistory(billingHistoryQuery.data.data.billing_history || []);
		}
	}, [billingHistoryQuery.data]);

	// Mutation to block campaigns.
	const blockCampaignMutation = useMutation({
		mutationKey: ["blockCampaignMutation", userId],
		mutationFn: () => authenticateAndPostData("/admin/block-user-campaigns/", {
			"id": userId
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			if (pageData) {
				setPageData({...pageData, campaigns_blocked: true})
			}
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	const toggleAdminAccessMutation = useMutation({
		mutationKey: ["toggleAdminAccessMutation", userId],
		mutationFn: (data: { user_id: number; is_admin: boolean }) => authenticateAndPostData("/admin/access-to-user/", data),
		gcTime: 0,
		retry: retryFn,
		onSuccess: (_, variables) => {
			if (pageData) {
					setPageData({
						...pageData,
						is_admin: variables.is_admin,
					});
				}
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	// Mutation to unblock campaigns.
	const unblockCampaignMutation = useMutation({
		mutationKey: ["unblockCampaignMutation", userId],
		mutationFn: () => authenticateAndPostData("/admin/unblock-user-campaigns/", {
			"id": userId
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			if (pageData) {
				setPageData({...pageData, campaigns_blocked: false})
			}
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	const triggerBlacklistCheckUserMutation = useMutation({
		mutationKey: ["triggerBlacklistCheckUser"],
		mutationFn: () => authenticateAndPostData(`/admin/trigger-user-blacklist-check/?user_id=${userId}`, {}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			enqueueSnackbar(`Blacklist Domain check for user ${pageData?.username} is in progress...`, {
				variant: "success",
			});
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	// Mutation to Verify user.
	const verifyUserMutation = useMutation({
		mutationKey: ["verifyUserMutation"],
		mutationFn: ({verify_email}: { verify_email: boolean }) =>
			authenticateAndPostData("/admin/admin-user-verify-email/", {
				"id": userId,
				"verify_email": verify_email,
			}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			if (pageData) {
				setPageData({...pageData, user_verified: !pageData.user_verified});
			}
			enqueueSnackbar("User verification status updated.", {
				variant: "success",
			});
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	// Mutation to toggle early access.
	const toggleEarlyAccessMutation = useMutation({
		mutationKey: ["toggleEarlyAccessMutation"],
		mutationFn: ({early_access}: { early_access: boolean }) =>
			authenticateAndPostData("/admin/toggle-early-access/", {
				"id": userId,
				"early_access": early_access,
			}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			if (pageData) {
				setPageData({...pageData, early_access: !pageData.early_access});
			}
			enqueueSnackbar("Early access status updated.", {
				variant: "success",
			});
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	// Mutation to User Spam ban.
	const toggleSpamBanMutation = useMutation({
		mutationKey: ["toggleEarlyAccessMutation"],
		mutationFn: ({spam_ban}: { spam_ban: boolean }) =>
			authenticateAndPostData("/admin/toggle-spam-ban/", {
				"id": userId,
				"spam_ban": spam_ban,
			}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			if (pageData) {
				setPageData({...pageData, spam_ban: !pageData.spam_ban});
			}
			enqueueSnackbar("Spam ban status updated.", {
				variant: "success",
			});
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	// Mutation to update the user's bounce limit.
	const updateBounceLimitMutation = useMutation({
		mutationKey: ["updateBounceLimitMutation", userId],
		mutationFn: ({bounce_limit}: { bounce_limit: number }) =>
			authenticateAndPostData("/admin/update-bounce-limit/", {
				"id": userId,
				"bounce_limit": bounce_limit,
			}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: (data) => {
			const updated = data.data.bounce_limit;
			if (pageData) {
				setPageData({...pageData, bounce_limit: updated});
			}
			setBounceLimit(String(updated));
			enqueueSnackbar("Bounce limit updated.", {
				variant: "success",
			});
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	const parsedBounceLimit = Number(bounceLimit);
	const bounceLimitInvalid = bounceLimit.trim() === "" || Number.isNaN(parsedBounceLimit)
		|| parsedBounceLimit < 0 || parsedBounceLimit > 100;

	function handleBlacklistCheckUser() {
		triggerBlacklistCheckUserMutation.mutate()
	}

	const billingColumns: CSVColumn<BillingHistoryItem>[] = [
		{ header: "Subscription ID", accessor: "subscription_id" },
		{ header: "Workspace Name", accessor: "workspace_name" },
		{ header: "Plan Name", accessor: "plan_name" },
		{ header: "Amount", accessor: "plan_amount" },
		{ header: "Status", accessor: "plan_status" },
		{ header: "Is Last Plan", accessor: (row) => (row.last_plan ? "Yes" : "No") }, // Custom formatter
	];

	// Usage inside click handler:
	const handleExportBilling = async () => {
        setExportLoading(true);
        try {            
            const response = await authenticateAndFetchData(
                `/admin/user-billing-history/?user_id=${userId}&export_all=true`
            );

            const allBillingHistory = response?.data?.data?.billing_history || [];

            if (!allBillingHistory.length) {
                enqueueSnackbar("No data available to export.", { variant: "error" });
                return;
            }

            const success = exportToCSV(
                allBillingHistory,
                billingColumns,                
				getExportFilename(user?.email ?? "unknown", "BillingHistory")
            );

            if (success) {
                enqueueSnackbar("Billing history exported!", { variant: "success" });
            } else {
                enqueueSnackbar("Failed to generate CSV file.", { variant: "error" });
            }
        } catch (error) {
            console.error("Export error:", error);
            enqueueSnackbar("Failed to fetch billing history for export.", { variant: "error" });
        } finally {
            setExportLoading(false);
        }
    };

	const ExportBillingHistoryCsvButton = () => {
		return (
			<Button startIcon={<Downloading />}
				variant="contained"
				color="primary"
				onClick={() => {
					handleExportBilling();
				}}
				disabled={exportLoading}
			>
				{exportLoading ? "Exporting..." : "Export"}
			</Button>
		)
	}

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (pageDataQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (pageDataQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Stack direction={"column"} spacing={2} sx={{pb: 4, height: "100%"}}>
				<Stack direction={"row"} spacing={1} alignItems={"center"} sx={{ flexWrap: "wrap" }}>
					<IconButton onClick={() => navigate(-1)}><ArrowBack /></IconButton>
					<Typography 
						fontWeight={"bold"} 
						variant={"h5"} 
						sx={{ 
							fontSize: { xs: "1.2rem", sm: "1.5rem" }, 
							wordBreak: "break-word" 
						}}
					>
						{pageData.username} ({pageData.email} <CopyButton text={pageData.email} />)
					</Typography>
				</Stack>
				<Divider/>
				<Stack direction={"column"} spacing={4}>
					{/* ---------------- Stat Cards ---------------- */}
					<Grid2 container spacing={3}>
						<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
							<StatCard
								icon={<FolderSpecial />}
								heading="Workspaces"
								value={pageData.total_workspaces}
								color="#3f51b5"
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
							<StatCard
								icon={<DomainVerification />}
								heading="Connected Domains"
								value={pageData.total_connected_domains}
								color="#2196f3"
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
							<StatCard
								icon={<Language />}
								heading="Email Subdomains"
								value={pageData.total_email_subdomains}
								color="#009688"
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
							<StatCard
								icon={<AlternateEmail />}
								heading="Email IDs"
								value={pageData.total_email_ids}
								color="#673ab7"
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
							<StatCard
								icon={<Campaign />}
								heading="Campaigns"
								value={pageData.total_campaigns}
								color="#ff5722"
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
							<StatCard
								icon={<Send />}
								heading="Emails Sent"
								value={pageData.total_emails_sent}
								color="#4caf50"
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
							<StatCard
								icon={<CreditScore />}
								heading="Total Email Credits Remaining"
								value={pageData.monthly_total_emails_remaining}
								color="#4caf50"
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
							<StatCard
								icon={<Block />}
								heading="Total Bounce Campaigns"
								value={pageData.cancelled_due_to_bounce_count}
								color="#f44336"
							/>
						</Grid2>
					</Grid2>

					{/* ---------------- Workspace, Domain, etc. links ---------------- */}
					<Box display={"flex"} flexDirection={"column"} justifyContent={"start"} alignItems={"start"}>
						<Typography variant={"h6"}>View All Data:</Typography>
						<Box display={"flex"} flexWrap={"wrap"} gap={1.5} sx={{ mt: 1, width: "100%" }}>
							<Button variant={"outlined"}
											component={Link}
											to={urls["adminUserWorkspaces"].replace(":userId", userId || "")}
											endIcon={<ChevronRight/>}>
								Workspaces
							</Button>
							<Button variant={"outlined"}
											component={Link}
											to={urls["adminUserConnectedDomains"].replace(":userId", userId || "")}
											endIcon={<ChevronRight/>}>
								Connected Domains
							</Button>
							<Button variant={"outlined"}
											component={Link}
											to={urls["adminUserEmails"].replace(":userId", userId || "")}
											endIcon={<ChevronRight/>}>
								Emails
							</Button>
							<Button variant={"outlined"}
											component={Link}
											to={urls["adminUserCampaigns"].replace(":userId", userId || "")}
											endIcon={<ChevronRight/>}>
								Campaigns
							</Button>
							<Button variant={"outlined"}
											component={Link}
											to={urls["adminUserSendingHistory"].replace(":userId", userId || "")}
											endIcon={<ChevronRight/>}>
								Sending History
							</Button>
							<Button variant={"outlined"}
											component={Link}
											to={urls["adminUserCreditLogs"].replace(":userId", userId || "")}
											endIcon={<ChevronRight/>}>
								Credit Logs
							</Button>
							<Button variant={"outlined"}
											component={Link}
											to={urls["adminUserIntegrations"].replace(":userId", userId || "")}
											endIcon={<ChevronRight/>}>
								Integrations
							</Button>
							<Button variant={"outlined"}
											component={Link}
											to={urls["adminUserContactList"].replace(":userId", userId || "")}
											endIcon={<ChevronRight/>}>
								Contact list
							</Button>
							<Button variant={"outlined"}
											component={Link}
											to={urls["adminUserEmailVerificationLists"].replace(":userId", userId || "")}
											endIcon={<ChevronRight/>}>
								Email Verification Submissions
							</Button>
							<Button variant={"outlined"}
											component={Link}
											to={urls["adminUserDeliverymanAPIKey"].replace(":userId", userId || "")}
											endIcon={<ChevronRight/>}>
								Deliveryman API Keys
							</Button>
						</Box>
					</Box>

					{/* Block Campaigns */}
					<Box display={"flex"} flexDirection={"column"} justifyContent={"start"} alignItems={"start"}>
						<Typography variant={"h6"}>
							Block/Unblock Campaigns For This User:
						</Typography>
						<Typography variant={"body2"}>
							<b>Note</b>: Campaign cancellation takes place in backround. It might
							take some time based on how many schedules need to be deleted.
						</Typography>
						{pageData.campaigns_blocked ?
							<Button variant={"contained"}
											color={"success"}
											startIcon={<Cancel/>}
											sx={{mt: 1}}
											onClick={async () => {
												const unblockConfirmed = await dialogs.confirm(
													`Remove campaign creation restriction from this account?`,
													{
														title: "Unblock Campaigns",
														cancelText: "No",
														okText: "Yes",
													}
												);
												if (unblockConfirmed) {
													unblockCampaignMutation.mutate();
												}
											}}>
								Unblock Campaigns
							</Button> :
							<Button variant={"contained"}
											color={"error"}
											startIcon={<Cancel/>}
											sx={{mt: 1}}
											onClick={async () => {
												const blockConfirmed = await dialogs.confirm(
													`Are you sure you want to block campaigns for this user? All campaigns that haven't 
													finished yet will be cancelled and  they will not be able to create any new campaign. 
													Campaign cancellations cannot be undone. Proceed?`,
													{
														title: "Block Campaigns",
														cancelText: "No",
														okText: "Proceed",
													}
												);
												if (blockConfirmed) {
													blockCampaignMutation.mutate();
												}
											}}>
								Block Campaigns
							</Button>}
					</Box>
					<Box display={"flex"} flexDirection={"column"} justifyContent={"start"} alignItems={"start"}>
						<Typography variant={"h6"}>
							Add New Domain:
						</Typography>
						<Button variant={"contained"}
										size={"small"}
										startIcon={<Add/>}
										disabled={false}
										onClick={() => {
											setOpenAddUpdatePost(true)
										}}>
							Add New Domain
						</Button>
					</Box>
					{/* Block Campaigns */}
					<Box display={"flex"} flexDirection={"column"} justifyContent={"start"} alignItems={"start"}>
						<Typography variant={"h6"}>
							Verify User Email:
						</Typography>
						{pageData.user_verified ? (
							<Button
								variant="contained"
								color="error"
								startIcon={<Cancel/>}
								sx={{mt: 1}}
								onClick={() => {
									verifyUserMutation.mutate({verify_email: false});
								}}
							>
								Unverify User Email
							</Button>
						) : (
							<Button
								variant="contained"
								color="success"
								// startIcon={<Cancel />}
								sx={{mt: 1}}
								onClick={() => {
									verifyUserMutation.mutate({verify_email: true});
								}}
							>
								Verify User Email
							</Button>
						)}
					</Box>

					{/* Early Access Toggle */}
					<Box display={"flex"} flexDirection={"column"} justifyContent={"start"} alignItems={"start"}>
						<Typography variant={"h6"}>
							Early Access:
						</Typography>
						<Typography variant={"body2"} sx={{mb: 1}}>
							Users with early access can bypass maintenance mode without admin privileges.
						</Typography>
						{pageData.early_access ? (
							<Button
								variant="contained"
								color="error"
								startIcon={<Cancel/>}
								sx={{mt: 1}}
								onClick={() => {
									toggleEarlyAccessMutation.mutate({early_access: false});
								}}
							>
								Disable Early Access
							</Button>
						) : (
							<Button
								variant="contained"
								color="success"
								sx={{mt: 1}}
								onClick={() => {
									toggleEarlyAccessMutation.mutate({early_access: true});
								}}
							>
								Enable Early Access
							</Button>
						)}
					</Box>

					<Box display={"flex"} flexDirection={"column"} justifyContent={"start"} alignItems={"start"}>
						<Typography variant={"h6"}>
							Add Email Credit:
						</Typography>
						<Button variant={"contained"}
										size={"small"}
										startIcon={<Add/>}
										disabled={false}
										onClick={() => {
											setOpenAddCredit(true)
										}}>
							Add Credit
						</Button>
					</Box>
					<Box display={"flex"} flexDirection={"column"} justifyContent={"start"} alignItems={"start"}>
						<Typography variant={"h6"}>
							Blacklist checker for user:
						</Typography>
						<Button variant={"contained"}
										size={"small"}
										disabled={false}
										onClick={() => {
											handleBlacklistCheckUser()
										}}>
							Blacklist Check
						</Button>
					</Box>

					{/* Spam Ban Toggle */}
					<Box display={"flex"} flexDirection={"column"} justifyContent={"start"} alignItems={"start"}>
						<Typography variant={"h6"}>
							Spam Ban:
						</Typography>
						<Typography variant={"body2"} sx={{mb: 1}}>
							Users with spam ban can not create campaign.
						</Typography>
						{pageData.spam_ban ? (
							<Button
								variant="contained"
								color="error"
								startIcon={<Cancel/>}
								sx={{mt: 1}}
								onClick={() => {
									toggleSpamBanMutation.mutate({spam_ban: false});
								}}
							>
								Disable Spam Ban
							</Button>
						) : (
							<Button
								variant="contained"
								color="success"
								sx={{mt: 1}}
								onClick={() => {
									toggleSpamBanMutation.mutate({spam_ban: true});
								}}
							>
								Enable Spam Ban
							</Button>
						)}
					</Box>

					{/* Bounce Limit */}
					<Box display={"flex"} flexDirection={"column"} justifyContent={"start"} alignItems={"start"}>
						<Typography variant={"h6"}>
							Bounce Limit:
						</Typography>
						<Typography variant={"body2"} sx={{mb: 1}}>
							A running campaign is automatically cancelled once its bounce rate reaches this
							percentage. Only applied after the campaign has sent 600 or more emails.
						</Typography>
						<Stack direction={"row"} spacing={1} alignItems={"center"} sx={{mt: 1}}>
							<TextField
								size={"small"}
								type={"number"}
								value={bounceLimit}
								onChange={(e) => setBounceLimit(e.target.value)}
								error={bounceLimitInvalid}
								helperText={bounceLimitInvalid ? "Enter a value between 0 and 100" : " "}
								slotProps={{
									input: {endAdornment: <Typography variant={"body2"}>%</Typography>},
									htmlInput: {min: 0, max: 100, step: 0.1},
								}}
								sx={{width: 160}}
							/>
							<Button
								variant={"contained"}
								disabled={bounceLimitInvalid
									|| parsedBounceLimit === pageData.bounce_limit
									|| updateBounceLimitMutation.isPending}
								onClick={() => {
									updateBounceLimitMutation.mutate({bounce_limit: parsedBounceLimit});
								}}
							>
								Save
							</Button>
						</Stack>
					</Box>

					<Box
							display="flex"
							flexDirection="column"
							justifyContent="start"
							alignItems="start"
						>
							<Typography variant="h6">
								Admin Access:
							</Typography>

							<Typography variant="body2">
								Grant or revoke administrator access for this user.
							</Typography>

							{pageData.is_admin ? (
								<Button
									variant="contained"
									color="error"
									startIcon={<Cancel />}
									sx={{ mt: 1 }}
									onClick={async () => {
										const confirmed = await dialogs.confirm(
											"Are you sure you want to remove admin access from this user?",
											{
												title: "Remove Admin Access",
												cancelText: "No",
												okText: "Yes",
											}
										);

										if (confirmed) {
											toggleAdminAccessMutation.mutate({
												user_id: pageData.user_id,
												is_admin: false,
											});
										}
									}}
								>
									Remove Admin Access
								</Button>
							) : (
								<Button
									variant="contained"
									color="success"
									startIcon={<Check />}
									sx={{ mt: 1 }}
									onClick={async () => {
										const confirmed = await dialogs.confirm(
											"Are you sure you want to grant admin access to this user? Admin users will have elevated permissions throughout the application.",
											{
												title: "Grant Admin Access",
												cancelText: "No",
												okText: "Grant",
											}
										);

										if (confirmed) {
											toggleAdminAccessMutation.mutate({
												user_id: pageData.user_id,
												is_admin: true,
											});
										}
									}}
								>
									Grant Admin Access
								</Button>
							)}
						</Box>
					<Divider/>

					{/* ---------------- Plan Billing History Table ---------------- */}
					<Box display={"flex"} flexDirection={"column"} justifyContent={"start"} alignItems={"start"}>
					<Typography variant={"h6"} sx={{ mb: 2 }}>Plan Billing History:</Typography>
					<Box sx={{ height: { xs: 400, md: 600 }, width: "100%" }}>
							<ServerSideDataTable
								columns={billingHistoryColumnDefs}
								rows={billingHistory}
								noRowsText="No billing history found"
								loading={billingHistoryQuery.isLoading}
								actions={<ExportBillingHistoryCsvButton />}
							/>
						</Box>
					</Box>
					<Divider/>

				</Stack>
				<AddCreditPopup open={openAddCredit}
												userId={userId!}
												workspaceDetails={pageData.workspace_details}
												workspace={workspace}
												onClose={() => setOpenAddCredit(false)}
												onSuccess={() => {
													setOpenAddCredit(false);
													pageDataQuery.refetch().then()
												}}/>
				<SelectWorkspacePopup open={openAddUpdatePost}
															userId={userId!}
															workspaceDetails={pageData.workspace_details}
															workspace={workspace}
															setWorkspaces={setWorkspaces}
															onClose={() => setOpenAddUpdatePost(false)}
															onSuccess={() => {
																setOpenAddUpdatePost(false);
															}}/>
			</Stack>
		)

	} else {
		// Ideally it should not reach here.
		return <></>
	}
}

function AddCreditPopup(props: {
	open: boolean,
	userId: string
	workspaceDetails: WorkspaceDetail[],
	workspace: WorkspaceDetail | null,
	onClose: () => void,
	onSuccess: () => void,
}) {
	const {enqueueSnackbar} = useSnackbar();

	const [workspace, setWorkspaces] = useState<WorkspaceDetail | null>(null);
	const [credits, setCredits] = useState<number | null>(null)


	const AddRemoveCreditMutation = useMutation({
		mutationKey: ["addRemoveCreditMutation"],
		mutationFn: ({addRemove}: { addRemove: string }) =>
			authenticateAndPostData("/admin/admin-add-remove-email-credit/", {
				workspaceId: workspace?.id,
				credits: credits,
				addRemove: addRemove,
			}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: (_data, variables) => {
			enqueueSnackbar(`Credits ${variables.addRemove == "Add" ? 'added' : 'removed'} Successfully`, {
				variant: "success",
			});
			setCredits(null)
			setWorkspaces(null)
			props.onSuccess()
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

	const handleAddRemoveCredits = (addRemove: string) => {
		AddRemoveCreditMutation.mutate({addRemove})
	}

	const handleWorkspaceSelect = (newValue: WorkspaceDetail | null) => {
		setWorkspaces(newValue);
	};

	return (
		<Dialog
			open={props.open}
			onClose={props.onClose}
			maxWidth="sm"
		>
			<DialogTitle>Add Or Remove Email Credit</DialogTitle>
			<DialogContent>
				<Stack direction={"column"} spacing={1}>
					<Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
						Select workspace
					</Typography>
					<Autocomplete
						options={props.workspaceDetails}
						value={workspace}
						onChange={(_, newValue) => handleWorkspaceSelect(newValue)}
						getOptionLabel={(option) => option.name}
						renderInput={(params) => (
							<TextField {...params} label="Workspace"/>
						)}
						PopperComponent={(popperProps) => (
							<Popper {...popperProps} style={{zIndex: 2000}}/>
						)}
					/>
					<Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
						Enter No of Credit
					</Typography>
					<TextField variant="outlined"
										 size={"small"}
										 value={credits}
										 sx={{maxWidth: 500}}
										 onChange={(e) => setCredits(Number(e.target.value))}/>
					{/* <Stack direction={"row"} spacing={2} alignItems={"center"}> */}
					<DialogActions sx={{px: 3, pb: 2}}>
						<Button variant="contained" color={"primary"} size={"small"} startIcon={<Add/>} onClick={() => {
							handleAddRemoveCredits("Add");
						}} disabled={!workspace || !credits}>
							Add
						</Button>
						<Button variant="contained" color={"error"} size={"small"} startIcon={<Delete/>} onClick={() => {
							handleAddRemoveCredits("Remove");
						}} disabled={!workspace || !credits}>
							Remove
						</Button>
						<Button variant="contained" color={"inherit"} size={"small"} startIcon={<Close/>} onClick={() => {
							setCredits(null);
							setWorkspaces(null);
							props.onClose();
						}}>
							Close
						</Button>
					</DialogActions>
					{/* </Stack> */}
				</Stack>
			</DialogContent>
		</Dialog>
	);
}


export function SelectWorkspacePopup(props: {
	open: boolean,
	userId: string
	workspaceDetails: WorkspaceDetail[],
	workspace: WorkspaceDetail | null,
	setWorkspaces: React.Dispatch<React.SetStateAction<WorkspaceDetail | null>>,
	onClose: () => void,
	onSuccess: () => void,
	url?: string
}) {

	const navigate = useNavigate();

	const handleWorkspaceSelect = (newValue: WorkspaceDetail | null) => {
		props.setWorkspaces(newValue);
		if (newValue && props.userId) {
			if (props.url) {
				const finalUrl = props.url
					.replace(":userId", props.userId)
					.replace(":workspaceId", String(newValue.id));
				navigate(finalUrl);
			} else {
				navigate(`/admin/users/${props.userId}/${newValue.id}/connect-domain`);
			}
		}
	};


	return (
		<Dialog
			open={props.open}
			onClose={props.onClose}
			// PaperProps={{
			//     style: {
			//         minWidth: 300,
			//         maxWidth: 400,
			//         padding: "16px",
			//         borderRadius: "12px"
			//     }
			// }}
			maxWidth="sm"
		>
			<DialogTitle>Select workspace to add new domain</DialogTitle>
			<DialogContent>
				<Autocomplete
					options={props.workspaceDetails}
					value={props.workspace}
					onChange={(_, newValue) => handleWorkspaceSelect(newValue)}
					getOptionLabel={(option) => option.name}
					renderInput={(params) => (
						<TextField {...params} label="Workspace"/>
					)}
					PopperComponent={(popperProps) => (
						<Popper {...popperProps} style={{zIndex: 2000}}/>
					)}
				/>
			</DialogContent>
		</Dialog>
	);
}
